import React, { useCallback, useEffect, useState } from 'react';
import { Table, Card, Btn, Modal, Input, Badge, RNLLoader, Spinner } from '../components/UI';
import { fmtNum, fmtDate, errorText } from '../utils';
import * as api from '../api';
import { useNavigate } from 'react-router-dom';

/**
 * Administrative management of requisitions and payment vouchers.
 *
 * Deliberately outside the normal queues: records are listed here regardless
 * of where they sit in their approval chain, and can be removed from it. That
 * is the point — it is the tool for cleaning up test data and mistakes that
 * the workflow itself gives no way out of.
 *
 * Structure mirrors the rest of Admin: a card picker, then a list.
 */

const SECTIONS = [
  {
    key: 'purchase_request',
    icon: '📄',
    title: 'Enterprise Requisition',
    color: '#0b318bff',
    desc: 'Every requisition raised, at any stage. Edit or permanently remove one along with its approval history.',
  },
  {
    key: 'payment_voucher',
    icon: '🧾',
    title: 'Payment Voucher',
    color: '#0b318bff',
    desc: 'Every payment voucher raised, at any stage. Edit or permanently remove one along with its approval history.',
  },
  {
    key: 'material_request',
    icon: '🏗️',
    title: 'Material Request',
    color: '#0b318bff',
    desc: 'Every material request raised, at any stage. Permanently remove one along with its workflow history.',
  },
];

const PurgeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
    <line x1="10" y1="11" x2="14" y2="15" /><line x1="14" y1="11" x2="10" y2="15" />
  </svg>
);

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

// Records are paged on the server. The list is an administrative archive —
// it only grows — so pulling every row to filter in the browser stops scaling
// the moment these tables get busy. Both the page of rows and the search term
// are the server's job.
const PAGE_SIZE = 25;

// One row more than the page displays. The requisition endpoint returns a bare
// array with no total, so this spare row is what tells us a next page exists;
// it is sliced off before rendering.
const PROBE = PAGE_SIZE + 1;

// Vouchers come back wrapped with a count, requisitions as a plain array.
const rowsOf = (res) => (Array.isArray(res) ? res : (res?.data || res?.vouchers || []));

export default function RequestManagementTab({ toast, section, setSection, access = {} }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // What the user is typing, and what the server has actually been asked for.
  // Kept apart so a request isn't fired on every keystroke.
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  // Only the voucher endpoint reports a total; requisitions leave this null
  // and the footer falls back to showing the range alone.
  const [total, setTotal] = useState(null);

  const [purge, setPurge] = useState(null);       // { row, impact }
  const [purgePreparing, setPurgePreparing] = useState(null);
  const [confirmRef, setConfirmRef] = useState('');
  const [purging, setPurging] = useState(false);
  const [purgeError, setPurgeError] = useState(null);

  // Debounce the search box. Resetting to the first page on every new term is
  // deliberate: staying on page 4 of the old result set would show an empty
  // table for a search that does have matches.
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery((prev) => (prev === search.trim() ? prev : search.trim()));
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    if (!section) { setRows([]); return; }
    setLoading(true);
    try {
      api.clearApiCache();
      const skip = page * PAGE_SIZE;
      let batch = [];
      let count = null;
      if (section === 'purchase_request') {
        batch = rowsOf(await api.listAllPRs(skip, PROBE, query));
      } else if (section === 'material_request') {
        const res = await api.listAllMRs(skip, PROBE, query);
        const items = Array.isArray(res) ? res : (res?.items || []);
        batch = items;
        count = typeof res?.total === 'number' ? res.total : null;
      } else {
        const res = await api.listPaymentVouchers({ skip, limit: PROBE, search: query });
        batch = rowsOf(res);
        count = typeof res?.total_count === 'number' ? res.total_count : null;
      }
      setHasNext(batch.length > PAGE_SIZE);
      setRows(batch.slice(0, PAGE_SIZE));
      setTotal(count);
    } catch (e) {
      toast.show(errorText(e), 'error');
      setRows([]); setHasNext(false); setTotal(null);
    }
    setLoading(false);
  }, [section, page, query, toast]);

  useEffect(() => { load(); }, [load]);

  // Requisitions and vouchers name their fields differently; one accessor
  // keeps the table below from branching on the section in every cell.
  const isPR = section === 'purchase_request';
  const isMR = section === 'material_request';
  const refOf = (r) => (isPR ? r.pr_number : isMR ? r.request_ref : r.voucher_no) || '—';
  const idOf = (r) => r.id || r.purchase_request_id || r.payment_voucher_id || r.request_id;
  const titleOf = (r) => (isPR ? r.title : isMR ? (r.building || r.request_ref) : r.payee_name || r.description) || '—';
  const whoOf = (r) => (isPR ? r.requested_by_name : isMR ? r.requested_by_name : r.created_by_name) || '—';
  const amountOf = (r) => (isPR ? r.total_estimated_cost : isMR ? null : r.net_payable ?? r.gross_amount) || 0;
  const statusOf = (r) => (r.request_status || r.status || '').toUpperCase();
  const dateOf = (r) => r.created_at || r.voucher_date || r.request_date || r.requested_date;

  const openPurge = async (row) => {
    if (purgePreparing) return;
    setPurgeError(null); setConfirmRef('');
    setPurgePreparing(idOf(row));
    try {
      const impact = await api.getRecordDeleteImpact(section, idOf(row));
      setPurge({ row, impact });
    } catch (e) {
      toast.show(errorText(e), 'error');
    }
    setPurgePreparing(null);
  };

  const runPurge = async () => {
    if (!purge || purging) return;
    setPurging(true); setPurgeError(null);
    try {
      const res = await api.hardDeleteRecord(section, idOf(purge.row), confirmRef.trim());
      setPurge(null);
      toast.show(res?.message || 'Record deleted');
      // Deleting the only row on a page would otherwise leave the admin
      // staring at an empty table with a Previous button as the only clue.
      if (rows.length === 1 && page > 0) setPage((n) => n - 1);
      else load();
    } catch (e) {
      setPurgeError(errorText(e));
    }
    setPurging(false);
  };

  // ── Card picker ────────────────────────────────────────────────────────────
  if (!section) {
    return (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {SECTIONS.map((s) => (
            <Card key={s.key} onClick={() => { setSection(s.key); setSearch(''); setQuery(''); setPage(0); }}
              style={{ padding: 22, cursor: 'pointer', borderLeft: `4px solid ${s.color}` }}>
              <div style={{ fontSize: 26, marginBottom: 10 }}>{s.icon}</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: s.color, marginBottom: 6 }}>{s.title}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{s.desc}</div>
            </Card>
          ))}
        </div>
      </>
    );
  }

  const meta = SECTIONS.find((s) => s.key === section);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>
          {meta.icon} {meta.title}
        </span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search by reference, title, ${isPR ? 'requester' : 'payee'} or status…`}
          style={{
            flex: '1 1 420px', maxWidth: 640, boxSizing: 'border-box', padding: '9px 12px',
            border: '1.5px solid var(--border-strong, #94a3b8)', borderRadius: 10,
            fontSize: 13, fontFamily: 'var(--font)', background: 'var(--bg-surface)',
            color: 'var(--text-primary)', outline: 'none',
          }}
        />
        {search.trim() !== query && (
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Searching…</span>
        )}
      </div>

      {loading ? <RNLLoader title="Loading records…" /> : (
        <Card>
          <Table
            cols={[
              { key: 'ref', label: 'REFERENCE', render: (_, r) => <strong style={{ color: 'var(--accent)' }}>{refOf(r)}</strong> },
              { key: 'title', label: isPR ? 'TITLE' : isMR ? 'BUILDING' : 'PAYEE', render: (_, r) => titleOf(r) },
              { key: 'who', label: (isPR || isMR) ? 'REQUESTER' : 'RAISED BY', render: (_, r) => whoOf(r) },
              { key: 'date', label: 'DATE', render: (_, r) => (dateOf(r) ? fmtDate(dateOf(r)) : '—') },
              ...(!isMR ? [{ key: 'amount', label: 'AMOUNT', render: (_, r) => `₦${fmtNum(amountOf(r))}` }] : []),
              { key: 'status', label: 'STATUS', render: (_, r) => <Badge color="gray">{(r.status || r.request_status || '—').toString().toUpperCase()}</Badge> },
              {
                key: 'actions', label: '', render: (_, r) => {
                  const busy = purgePreparing === idOf(r);
                  const openUrl = isPR
                    ? `/requisitions?pr=${idOf(r)}`
                    : isMR ? null
                    : `/payment-vouchers?voucher=${idOf(r)}`;
                  return (
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                      {!isMR && (
                        <Btn variant="ghost" title="Open this record" disabled={!!purgePreparing}
                          style={{ padding: '6px', opacity: purgePreparing ? 0.45 : 1 }}
                          onClick={() => window.open(openUrl, '_blank')}>
                          <EditIcon />
                        </Btn>
                      )}
                      {access.manage && (
                      <Btn
                        variant="danger"
                        disabled={!!purgePreparing}
                        title={busy ? 'Checking what would be deleted…' : 'Permanently delete this record and its approval flow'}
                        style={{
                          padding: '6px', background: '#7f1d1d', color: '#fff', border: '1px solid #7f1d1d',
                          cursor: busy ? 'wait' : (purgePreparing ? 'not-allowed' : 'pointer'),
                          opacity: purgePreparing && !busy ? 0.45 : 1,
                        }}
                        onClick={() => openPurge(r)}
                      >
                        {busy ? <Spinner size={15} /> : <PurgeIcon />}
                      </Btn>
                      )}
                    </div>
                  );
                }
              },
            ]}
            rows={rows}
            emptyMsg={query ? `No records match "${query}".` : 'No records found.'}
          />

          {/* Hidden on a single page of results — Prev/Next with nowhere to go
              is just noise. `total` is only known for vouchers, so the range
              reads "26–50 of 214" there and "26–50" for requisitions. */}
          {(page > 0 || hasNext) && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, flexWrap: 'wrap', paddingTop: 14, marginTop: 4,
              borderTop: '1px solid var(--border)',
            }}>
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {rows.length > 0
                  ? <>Showing <strong style={{ color: 'var(--text-primary)' }}>
                      {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + rows.length}
                    </strong>{total != null && <> of <strong style={{ color: 'var(--text-primary)' }}>{total}</strong></>}
                    {total != null && total === 1 ? ' record' : ' records'}</>
                  : 'No records on this page.'}
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Btn variant="secondary" disabled={page === 0 || loading}
                  onClick={() => setPage((n) => Math.max(0, n - 1))}>
                  ‹ Previous
                </Btn>
                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', minWidth: 62, textAlign: 'center' }}>
                  Page <strong style={{ color: 'var(--text-primary)' }}>{page + 1}</strong>
                  {total != null && <> of {Math.max(1, Math.ceil(total / PAGE_SIZE))}</>}
                </span>
                <Btn variant="secondary" disabled={!hasNext || loading}
                  onClick={() => setPage((n) => n + 1)}>
                  Next ›
                </Btn>
              </div>
            </div>
          )}
        </Card>
      )}

      {purge && (() => {
        const ref = purge.impact?.reference || refOf(purge.row);
        const matches = confirmRef.trim().toLowerCase() === (ref || '').toLowerCase();
        const { deleted = [], detached = [], total_deleted = 0, total_detached = 0 } = purge.impact || {};
        const line = (d, i, colour) => (
          <div key={d.table + d.label} style={{
            display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 12px',
            fontSize: 12.5, borderTop: i ? '1px solid var(--border)' : 'none',
          }}>
            <span style={{ color: 'var(--text-primary)' }}>{d.label}</span>
            <strong style={{ color: colour }}>{d.count}</strong>
          </div>
        );
        return (
          <Modal title="Permanently Delete Record" width={620}
            headerBg="#7f1d1d" headerColor="#ffffff"
            onClose={purging ? () => { } : () => setPurge(null)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '4px 0' }}>
              <div style={{
                display: 'flex', gap: 12, alignItems: 'flex-start', background: 'var(--red-dim)',
                border: '1px solid rgba(239,68,68,0.35)', borderRadius: 8, padding: '12px 14px',
              }}>
                <span style={{ fontSize: 18, lineHeight: 1.1 }}>&#9888;</span>
                <div style={{ fontSize: 13, color: 'var(--red)', lineHeight: 1.6 }}>
                  <strong>This cannot be undone.</strong> {purge.impact?.entity_label} <strong>{ref}</strong> and
                  its approval history will be erased. There is no recovery short of a database restore.
                </div>
              </div>

              {total_deleted > 0 && (
                <div>
                  <div style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>
                    Will be deleted ({total_deleted})
                  </div>
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                    {deleted.map((d, i) => line(d, i, 'var(--red)'))}
                  </div>
                </div>
              )}

              {total_detached > 0 && (
                <div>
                  <div style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>
                    Kept, but will no longer reference it ({total_detached})
                  </div>
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                    {detached.map((d, i) => line(d, i, 'var(--text-muted)'))}
                  </div>
                </div>
              )}

              <div>
                <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', display: 'block', marginBottom: 6 }}>
                  Type <span style={{ fontFamily: 'var(--mono)', color: 'var(--red)' }}>{ref}</span> to confirm
                </label>
                <Input value={confirmRef} onChange={setConfirmRef} placeholder={ref} />
              </div>

              {purgeError && (
                <div style={{ fontSize: 12.5, color: 'var(--red)', background: 'var(--red-dim)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '9px 12px', lineHeight: 1.55 }}>
                  {purgeError}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Btn variant="ghost" disabled={purging} onClick={() => setPurge(null)}>Cancel</Btn>
                <Btn variant="danger" loading={purging} disabled={!matches}
                  style={{ background: '#7f1d1d', color: '#fff', border: '1px solid #7f1d1d' }}
                  onClick={runPurge}>
                  {purging ? 'Deleting…' : 'Delete Permanently'}
                </Btn>
              </div>
            </div>
          </Modal>
        );
      })()}
    </>
  );
}
